import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { UsersIcon, CheckCircle2Icon, BuildingIcon, FlaskConicalIcon, AwardIcon, ShieldCheckIcon, ArrowRightIcon } from 'lucide-react';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const specialists = ['Environmental Scientists', 'Geologists', 'Hydrogeologists', 'Ecologists', 'Biologists', 'Environmental Engineers', 'Health & Safety Specialists', 'GIS & Data Analysts'];
const memberships = ['Nigerian Environmental Society', 'Nigerian Conservation Foundation', 'International Environmental Forum', 'International Safety Quality Environment Association (UK)', 'International Association of Engineers & Computer Scientists'];
const certifications = ['General Health, Safety & Environment Certification', 'HSE Competence Level 3 (SPDC Accredited)', 'United Nations Environmental Law Training'];
const labPartners = [{
  city: 'Warri',
  region: 'Delta State'
}, {
  city: 'Lagos',
  region: 'Lagos State'
}, {
  city: 'Benin',
  region: 'Edo State'
}, {
  city: 'Abuja',
  region: 'FCT'
}];
export function TeamPage() {
  return <div className="bg-gesl-offWhite" data-id="element-666">
      <PageHeader title="Technical Capacity" subtitle="Our multidisciplinary team delivers comprehensive environmental solutions backed by strong QA/QC systems." data-id="element-667" />

      {/* Specialists + QA Section */}
      <section className="py-24 lg:py-32 bg-white" data-id="element-668">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-669">
          <div className="grid lg:grid-cols-12 gap-16 items-start" data-id="element-670">
            {/* Left: Specialists Grid */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:col-span-7" data-id="element-671">
              <motion.div variants={fadeUpVariants} className="flex items-center gap-3 mb-4" data-id="element-672">
                <div className="h-px w-8 bg-gesl-gold" data-id="element-673" />
                <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-674">
                  Our Experts
                </span>
              </motion.div>

              <motion.h2 variants={fadeUpVariants} className="text-3xl md:text-4xl font-bold text-gesl-dark mb-6 leading-tight" data-id="element-675">
                Multidisciplinary Team
              </motion.h2>

              <motion.p variants={fadeUpVariants} className="text-lg text-gray-600 mb-10 leading-relaxed" data-id="element-676">
                GESL deploys multidisciplinary project teams to ensure
                comprehensive environmental solutions. For complex projects, we
                engage respected external associates and subject-matter
                specialists.
              </motion.p>

              <div className="grid sm:grid-cols-2 gap-4" data-id="element-677">
                {specialists.map((spec, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-gesl-offWhite p-5 rounded-sm flex items-center gap-4 group hover:bg-gesl-warmWhite transition-colors duration-300 border border-gray-100" data-id="element-678">
                    <div className="flex flex-col items-center justify-center" data-id="element-679">
                      <span className="text-gesl-gold text-[10px] font-bold tracking-wider mb-1" data-id="element-680">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <UsersIcon className="h-5 w-5 text-gesl-green group-hover:text-gesl-gold transition-colors duration-300" data-id="element-681" />
                    </div>
                    <span className="font-semibold text-gesl-dark text-sm leading-tight" data-id="element-682">
                      {spec}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Right: QA Panel */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="lg:col-span-5 bg-gesl-dark text-white p-10 md:p-12 rounded-sm shadow-2xl relative overflow-hidden" data-id="element-683">
              {/* Decorative background element */}
              <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-gesl-green rounded-full blur-3xl opacity-20" data-id="element-684" />

              <div className="relative z-10" data-id="element-685">
                <div className="h-14 w-14 bg-white/5 rounded-full flex items-center justify-center mb-8" data-id="element-686">
                  <BuildingIcon className="h-7 w-7 text-gesl-gold" data-id="element-687" />
                </div>

                <h3 className="text-2xl font-bold mb-4 tracking-tight" data-id="element-688">
                  Quality Assurance
                </h3>
                <p className="text-white/70 mb-10 leading-relaxed" data-id="element-689">
                  All operations follow Standard Operating Procedures supported
                  by strong QA/QC systems. We ensure accuracy, reliability, and
                  consistency in all deliverables.
                </p>

                <ul className="space-y-5" data-id="element-690">
                  {['Peer review of all technical reports', 'Continuous internal auditing', 'SOP-driven field procedures', 'Chain-of-custody documentation', 'Compliance monitoring'].map((item, idx) => <motion.li key={idx} initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: 0.3 + idx * 0.1
                }} className="flex items-start group" data-id="element-691">
                      <CheckCircle2Icon className="h-5 w-5 text-gesl-gold mr-4 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" data-id="element-692" />
                      <span className="text-white/90 leading-relaxed" data-id="element-693">
                        {item}
                      </span>
                    </motion.li>)}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Memberships & Certifications Section */}
      <section className="py-24 lg:py-32 bg-gesl-warmWhite" data-id="element-694">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-695">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center max-w-3xl mx-auto mb-16" data-id="element-696">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-697">
              <div className="h-px w-8 bg-gesl-gold" data-id="element-698" />
              <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-699">
                Credentials
              </span>
              <div className="h-px w-8 bg-gesl-gold" data-id="element-700" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gesl-dark tracking-tight" data-id="element-701">
              Professional Certifications & Memberships
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16" data-id="element-702">
            {/* Memberships */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-703">
              <motion.h3 variants={fadeUpVariants} className="text-xl font-bold text-gesl-charcoal mb-6 flex items-center gap-3" data-id="element-704">
                <AwardIcon className="h-6 w-6 text-gesl-gold" data-id="element-705" />
                Memberships
              </motion.h3>
              <div className="space-y-4" data-id="element-706">
                {memberships.map((membership, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-white p-6 rounded-sm border-l-4 border-gesl-gold shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow duration-300" data-id="element-707">
                    <AwardIcon className="h-5 w-5 text-gesl-gold/50 flex-shrink-0" data-id="element-708" />
                    <span className="text-gesl-dark font-medium leading-relaxed" data-id="element-709">
                      {membership}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Certifications */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-710">
              <motion.h3 variants={fadeUpVariants} className="text-xl font-bold text-gesl-charcoal mb-6 flex items-center gap-3" data-id="element-711">
                <ShieldCheckIcon className="h-6 w-6 text-gesl-green" data-id="element-712" />
                Certifications
              </motion.h3>
              <div className="space-y-4" data-id="element-713">
                {certifications.map((cert, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-white p-6 rounded-sm shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:items-center gap-4 hover:shadow-md transition-shadow duration-300" data-id="element-714">
                    <ShieldCheckIcon className="h-6 w-6 text-gesl-green flex-shrink-0" data-id="element-715" />
                    <span className="text-gesl-dark font-medium leading-relaxed flex-grow" data-id="element-716">
                      {cert}
                    </span>
                    <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-gesl-green/10 text-gesl-green text-xs font-bold uppercase tracking-wider whitespace-nowrap self-start sm:self-auto" data-id="element-717">
                      Verified
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Laboratory Partners Section */}
      <section className="py-24 lg:py-32 bg-white" data-id="element-718">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-719">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center max-w-3xl mx-auto mb-16" data-id="element-720">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-721">
              <div className="h-px w-8 bg-gesl-gold" data-id="element-722" />
              <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-723">
                Nationwide Coverage
              </span>
              <div className="h-px w-8 bg-gesl-gold" data-id="element-724" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gesl-dark mb-6 tracking-tight" data-id="element-725">
              Laboratory Partnerships
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-726">
              We maintain working relationships with accredited laboratories
              across Nigeria to ensure accurate environmental sampling, testing,
              and reporting.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-727">
            {labPartners.map((lab, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-gesl-offWhite p-8 rounded-sm text-center border border-gray-100 group hover:bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-id="element-728">
                <div className="h-16 w-16 bg-gesl-green/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gesl-green transition-colors duration-300" data-id="element-729">
                  <FlaskConicalIcon className="h-8 w-8 text-gesl-green group-hover:text-white transition-colors duration-300" data-id="element-730" />
                </div>
                <h3 className="text-xl font-bold text-gesl-dark mb-2" data-id="element-731">
                  {lab.city}
                </h3>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-732">
                  {lab.region}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* HSE Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-id="element-733">
        {/* Background Image & Overlays */}
        <div className="absolute inset-0" data-id="element-734">
          <img src="https://cdn.magicpatterns.com/uploads/m7urnWeUwGJU2Hni8Hvy4D/image62.jpg" alt="GESL team in PPE at coastal project site" className="w-full h-full object-cover" data-id="element-735" />
          <div className="absolute inset-0 bg-gesl-dark/85 mix-blend-multiply" data-id="element-736" />
          <div className="absolute inset-0 bg-gradient-to-r from-gesl-dark via-gesl-dark/80 to-transparent" data-id="element-737" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-738">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-739">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} data-id="element-740">
              <div className="flex items-center gap-3 mb-6" data-id="element-741">
                <div className="h-px w-8 bg-gesl-gold" data-id="element-742" />
                <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-743">
                  Safety First
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 tracking-tight leading-tight" data-id="element-744">
                Health, Safety & Environment
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-lg" data-id="element-745">
                GESL maintains comprehensive HSE policies to ensure the safety
                of our personnel, communities, and the environment across all
                project operations.
              </p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="space-y-4" data-id="element-746">
              {['Field safety protocols for all operational activities', 'Risk assessment procedures prior to project commencement', 'Occupational health compliance monitoring', 'Regular staff HSE training and capacity building', 'Incident prevention and emergency response systems'].map((item, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-white/10 backdrop-blur-md border border-white/10 p-5 rounded-sm flex items-start group hover:bg-white/20 transition-colors duration-300" data-id="element-747">
                  <CheckCircle2Icon className="h-6 w-6 text-gesl-gold mr-4 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" data-id="element-748" />
                  <span className="text-white/90 font-medium leading-relaxed" data-id="element-749">
                    {item}
                  </span>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gesl-green to-gesl-dark relative overflow-hidden" data-id="element-750">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gesl-gold/10 rounded-full blur-3xl" data-id="element-751" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" data-id="element-752" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-753">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUpVariants} data-id="element-754">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight" data-id="element-755">
              Join Our Growing Team
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto font-light" data-id="element-756">
              We are always looking for talented environmental professionals to
              join our multidisciplinary team.
            </p>
            <button onClick={() => window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })} className="inline-flex items-center gap-2 px-8 py-4 bg-gesl-gold text-white font-bold rounded-sm hover:bg-white hover:text-gesl-dark transition-colors duration-300 shadow-lg group" data-id="element-757">
              Get In Touch
              <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-758" />
            </button>
          </motion.div>
        </div>
      </section>
    </div>;
}